<template>
  <base-view id="services-redesign">
    <div class="services-redesign">
      <!-- Hero -->
      <div
        class="relative w-full bg-center bg-cover"
        :style="{ backgroundImage: 'url(' + heroImage + ')' }"
      >
        <div class="absolute inset-0 hero-overlay"></div>
        <section class="relative flex flex-col items-center gap-4 max-w-3xl px-6 pt-16 pb-10 mx-auto text-center">
          <h1 class="m-0 text-4xl font-extrabold leading-tight text-gray-900">Our Services</h1>
          <p class="max-w-lg m-0 text-lg text-gray-700">
            Pick a plan type to see how it works, what it covers, and where to find the forms.
          </p>
        </section>
      </div>

      <!-- Plan grid -->
      <section class="grid-section">
        <div class="plan-grid">
          <button
            v-for="plan in plans"
            :key="plan.id"
            type="button"
            class="plan-card"
            :class="{ 'plan-card-selected': plan.id === selectedPlan }"
            :aria-pressed="plan.id === selectedPlan"
            @click="selectPlan(plan.id)"
          >
            <div
              class="plan-photo"
              :style="{ backgroundImage: 'url(' + planPhoto(plan.photo) + ')' }"
            ></div>
            <div class="plan-body">
              <div class="plan-name">{{ plan.name }}</div>
              <div class="plan-desc">{{ plan.shortDesc }}</div>
            </div>
          </button>
        </div>
      </section>

      <!-- Detail panel -->
      <section
        v-if="selected"
        id="plan-detail"
        ref="planDetail"
        class="detail-section"
      >
        <div class="detail-card">
          <div class="detail-header">
            <h2>{{ selected.name }}</h2>
            <button type="button" class="close-btn" @click="closeSelected">
              <span aria-hidden="true">✕</span> Close
            </button>
          </div>

          <p
            v-for="(p, i) in selected.description"
            :key="i"
            class="detail-desc"
          >
            {{ p }}
          </p>

          <div class="section-label">Key features</div>
          <div class="feature-list">
            <div
              v-for="(f, i) in selected.features"
              :key="i"
              class="feature-row"
            >
              <span class="feature-badge" aria-hidden="true">✓</span>
              <div class="feature-text">{{ f }}</div>
            </div>
          </div>

          <div class="section-label">Handouts &amp; resources</div>
          <div class="handout-row">
            <a
              v-for="(h, i) in selected.handouts"
              :key="i"
              :href="handoutHref(h.file)"
              target="_blank"
              rel="noopener noreferrer"
              class="handout-pill"
              >{{ h.label }} →<span class="sr-only">
                (opens in new tab)</span
              ></a
            >
            <router-link
              v-if="['fsa', 'hsa', 'dca', 'transit'].includes(selected.id)"
              :to="{ path: '/whats-covered', query: { plan: selected.id } }"
              class="covered-pill"
              >See what's covered →</router-link
            >
            <router-link
              :to="'/services/' + selected.id"
              class="details-pill"
              >View full plan details →</router-link
            >
          </div>
        </div>
      </section>

      <!-- Contact CTA -->
      <section class="cta-section">
        <div class="cta-card">
          <h2>Not sure which plan fits?</h2>
          <p>
            Talk to your broker or request a proposal and we'll help you compare options.
          </p>
          <div class="cta-buttons">
            <router-link to="/proposal" class="cta-btn cta-btn-white"
              >Request a Proposal</router-link
            >
            <a href="tel:8887221223" class="cta-btn cta-btn-outline"
              >Call 888.722.1223</a
            >
          </div>
        </div>
      </section>
    </div>
  </base-view>
</template>

<script>
import BaseView from "@/components/layout/BaseView.vue";

export default {
  name: "ServicesRedesign",
  components: { BaseView },
  data() {
    return {
      heroImage: require("@/assets/bg.jpg"),
      selectedPlan: null,
      plans: [
        {
          id: "cafeteria",
          name: "Cafeteria Plan",
          shortDesc:
            "Pre-tax dollars to help pay for health insurance and dependent care.",
          photo: "cafeteria.jpg",
          description: [
            "An employer-offered program that lets employees pay for certain expenses (like health insurance and dependent care) with pre-tax dollars, reducing taxable income for both sides."
          ],
          tags: ["Pre-Tax", "Employee Choice", "Irrevocable Elections"],
          features: [
            "Reduces taxable income for employees and payroll tax for employers",
            "Can bundle multiple pre-tax benefit options under one plan",
            "Governed by IRS Section 125"
          ],
          handouts: []
        },
        {
          id: "fsa",
          name: "Flexible Spending Account",
          shortDesc:
            "Pay copays, out-of-pocket medical costs, or dependent care with pre-tax dollars.",
          photo: "fsa.jpg",
          description: [
            "A front-loaded benefit account employees use to pay copays, out-of-pocket medical treatments or supplies, and dependent healthcare expenses.",
            "Because contributions are pre-tax, both the employee and employer save on taxes."
          ],
          tags: ["Pre-Tax", "Use It or Lose It", "Employer Front-Loaded"],
          features: [
            "Full annual election is available from day one of the plan year",
            "Unused funds generally don't roll over; check your employer's plan for rollover limits",
            "Debit card available for point-of-sale payment at eligible merchants"
          ],
          handouts: [
            { label: "FSA User Guide", file: "2026 Health FSA User Guide.pdf" },
            {
              label: "Limited Purpose FSA Guide",
              file: "2026 Limited Purpose Health FSA User Guide.pdf"
            }
          ]
        },
        {
          id: "dca",
          name: "Dependent Care Assistance",
          shortDesc:
            "Pre-tax funds for preschool, day camp, before/after school care, and daycare.",
          photo: "dca.jpg",
          description: [
            "A pre-tax benefit account used to pay for dependent care services such as preschool, summer day camp, before- or after-school programs, and daycare."
          ],
          tags: ["Pre-Tax", "$7,500 Annual Max", "Never Rolls Over"],
          features: [
            "Covers care for children under 13 or dependents unable to self-care",
            "Funds accrue as contributed, unlike FSA's full up-front election",
            "Care must allow you (and a spouse, if applicable) to work"
          ],
          handouts: [
            {
              label: "DCA User Guide",
              file: "2026 Dependent Care FSA User Guide.pdf"
            }
          ]
        },
        {
          id: "hsa",
          name: "Health Savings Account",
          shortDesc:
            "A tax-advantaged savings account for those enrolled in a high-deductible plan.",
          photo: "hsa.jpg",
          description: [
            "A tax-advantaged medical savings account available to employees enrolled in a high-deductible health plan (HDHP)."
          ],
          tags: ["No Deadline", "Tax-Free (Qualified Expenses)", "Portable"],
          features: [
            "Funds roll over year to year with no use-it-or-lose-it deadline",
            "Contributions, growth, and qualified withdrawals are all tax-free",
            "The account is yours even if you change employers"
          ],
          handouts: [
            { label: "HSA User Guide", file: "2027 HSA User Guide.pdf" }
          ]
        },
        {
          id: "hra",
          name: "Health Reimbursement Arrangement",
          shortDesc:
            "Employer-funded reimbursement for medical expenses and premiums.",
          photo: "hra.jpg",
          description: [
            "A benefit arrangement used to reimburse employees for out-of-pocket medical expenses and, in some designs, personal health insurance premiums."
          ],
          tags: ["Employer-Funded", "Rollover Varies", "Employer Sets Expenses"],
          features: [
            "Fully funded by the employer with no employee contributions required",
            "Employer sets the reimbursable expense categories and annual amount",
            "Unused funds can often be carried over based on plan design"
          ],
          handouts: [
            {
              label: "HRA Enrollment Guide",
              file: "RMR HRA Enrollment Guide.pdf"
            }
          ]
        },
        {
          id: "transit",
          name: "Qualified Transit/Parking",
          shortDesc:
            "Reduce commuting costs for transit, vanpooling, and parking.",
          photo: "transit.jpg",
          description: [
            "A benefit program allowing employees to reduce their commuting expenses for transit, vanpooling, and work-related parking costs using pre-tax dollars."
          ],
          tags: ["Pre-Tax", "$340/Month Each", "Rolls Over Monthly"],
          features: [
            "Separate monthly IRS limits for transit and parking",
            "Elections can typically be changed month to month",
            "Funds don't expire while you remain enrolled"
          ],
          handouts: [
            {
              label: "Transit User Guide",
              file: "2026 Qualified Transit User Guide.pdf"
            },
            {
              label: "Parking User Guide",
              file: "2026 Qualified Parking User Guide.pdf"
            }
          ]
        },
        {
          id: "wellness",
          name: "Wellness Accounts",
          shortDesc:
            "A taxable allowance to spend on wellness-related activities and products.",
          photo: "wellness.jpg",
          description: [
            "A personalized health program that provides employees with a taxable allowance to spend on wellness-related activities and products."
          ],
          tags: ["Employer-Funded", "Preventive Focus", "Varies by Plan Design"],
          features: [
            "Employer defines eligible wellness categories",
            "Funds are taxable, unlike FSA/HSA/HRA contributions",
            "Commonly used for gym memberships, fitness gear, and similar expenses"
          ],
          handouts: []
        },
        {
          id: "lsa",
          name: "Lifestyle Spending Accounts",
          shortDesc: "After-tax funds employees can use for everyday needs.",
          photo: "lifestyle.jpg",
          description: [
            "A personalized, employer-offered program that provides after-tax funds for employees' everyday needs, often broader in scope than a traditional wellness account."
          ],
          tags: ["Post-Tax (Taxable)", "Broad Eligible Expenses", "Employer-Defined"],
          features: [
            "Employer sets the funding amount and eligible categories",
            "Can cover a wide range of personal expenses based on employer setup",
            "Funds are taxable to the employee"
          ],
          handouts: [
            {
              label: "LSA Enrollment Guide",
              file: "RMR LSA Enrollment Guide.pdf"
            },
            { label: "LSA FAQs", file: "RMR LSA FAQs.pdf" }
          ]
        },
        {
          id: "cobra",
          name: "COBRA Administration",
          shortDesc:
            "Continue previous health coverage after job loss or reduced hours.",
          photo: "cobra.jpg",
          description: [
            "A health insurance program allowing employees to continue their previous group health coverage in the case of lost employment or a reduction in hours."
          ],
          tags: ["60-Day Election Window", "Up to 18–36 Months", "Participant Pays Full Premium"],
          features: [
            "Coverage generally continues for 18–36 months depending on the qualifying event",
            "The full premium (plus up to a 2% admin fee) is paid by the beneficiary",
            "Election and payment deadlines are strict, so tracking them carefully is important"
          ],
          handouts: [
            {
              label: "Employee Guide to COBRA",
              file: "Employee Guide to COBRA.pdf"
            }
          ]
        }
      ]
    };
  },
  computed: {
    selected() {
      return this.plans.find(p => p.id === this.selectedPlan) || null;
    }
  },
  created() {
    const plan = this.$route.query.plan;
    if (plan && this.plans.some(p => p.id === plan)) {
      this.selectedPlan = plan;
    }
  },
  mounted() {
    if (this.selectedPlan) {
      this.$nextTick(() => {
        const el = document.getElementById("plan-detail");
        if (el) el.scrollIntoView({ block: "start" });
      });
    }
  },
  methods: {
    selectPlan(id) {
      this.selectedPlan = id;
      this.$router
        .replace({ path: "/services", query: { plan: id } })
        .catch(() => {});
      this.$nextTick(() => {
        const el = document.getElementById("plan-detail");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    closeSelected() {
      this.selectedPlan = null;
      this.$router
        .replace({ path: "/services", query: {} })
        .catch(() => {});
    },
    planPhoto(filename) {
      return require(`@/assets/${filename}`);
    },
    handoutHref(filename) {
      return require(`@/assets/forms/${filename}`);
    }
  }
};
</script>

<style scoped>
.services-redesign {
  background: #fafafa;
}

/* ── Hero ── */
.hero-overlay {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.62) 0%,
    rgba(255, 255, 255, 0.88) 45%,
    #fafafa 75%,
    #fafafa 100%
  );
}

/* ── Plan grid ── */
.grid-section {
  max-width: 960px;
  margin: 0 auto;
  width: 100%;
  padding: 0 24px 48px;
}
.plan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}
.plan-card {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  margin: 0;
  width: 100%;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #dddddd;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.plan-card:hover {
  box-shadow: 0 10px 24px -10px rgba(23, 23, 23, 0.15);
}
.plan-card:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
}
.plan-card-selected {
  border: 1.5px solid #096843;
}
.plan-photo {
  width: 100%;
  height: 120px;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  flex: none;
}
.plan-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 20px 22px;
}
.plan-name {
  font-weight: 700;
  font-size: 15.5px;
  color: #171717;
}
.plan-desc {
  font-size: 13.5px;
  line-height: 1.5;
  color: #5e5d5d;
}

/* ── Detail panel ── */
.detail-section {
  max-width: 760px;
  margin: 0 auto;
  width: 100%;
  padding: 0 24px 72px;
  scroll-margin-top: 96px;
}
.detail-card {
  background: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 20px;
  padding: 36px;
}
.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.detail-header h2 {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #171717;
}
.close-btn {
  flex: none;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: #5e5d5d;
  white-space: nowrap;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.close-btn:hover {
  color: #096843;
}
.close-btn:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
  border-radius: 4px;
}
.detail-desc {
  font-size: 15px;
  line-height: 1.65;
  color: #303030;
  margin: 0 0 14px;
}
.section-label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #171717;
  margin: 24px 0 12px;
}
.feature-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}
.feature-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.feature-badge {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #defae6;
  color: #096843;
  font-size: 11px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  margin-top: 1px;
}
.feature-text {
  font-size: 14px;
  line-height: 1.55;
  color: #303030;
}
.handout-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.handout-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 9px;
  background: #f5f5f5;
  color: #303030;
  font-weight: 600;
  font-size: 13.5px;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.handout-pill:hover {
  background: #defae6;
  color: #096843;
}
.handout-pill:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
}
.covered-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 9px;
  background: #e3edf6;
  color: #1c4d78;
  font-weight: 700;
  font-size: 13.5px;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.covered-pill:hover {
  color: #153c5e;
}
.covered-pill:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
}
.details-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 9px;
  background: #096843;
  color: #ffffff;
  font-weight: 700;
  font-size: 13.5px;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.details-pill:hover {
  opacity: 0.9;
}
.details-pill:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
}

/* ── Contact CTA ── */
.cta-section {
  padding: 0 24px 80px;
}
.cta-card {
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  padding: 44px;
  border-radius: 20px;
  background: linear-gradient(135deg, #096843, #1c4d78);
}
.cta-card h2 {
  margin: 0 0 10px;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #ffffff;
}
.cta-card p {
  margin: 0 0 24px;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
}
.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}
.cta-btn {
  padding: 13px 24px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 14.5px;
  text-decoration: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.15s ease;
}
.cta-btn:hover {
  opacity: 0.85;
}
.cta-btn:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}
.cta-btn-white {
  background: #ffffff;
  color: #096843;
}
.cta-btn-outline {
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
}
</style>
